// Resize + preprocess a captured / uploaded photo before sending to Claude.
//
// Three passes, all in the browser, zero dependencies:
//   1. EXIF orientation — iPhone photos are often stored landscape-native with
//      a "rotate on display" flag. Without this, portrait shots arrive sideways.
//   2. Mild contrast + brightness boost to make textbook print pop, which
//      measurably improves Vision OCR accuracy on dim/indoor photos.
//   3. Resize to maxDim (default 1280 px long edge) and re-encode as JPEG.
//      Typical iPad shot drops from ~6 MB to ~400 KB — big bandwidth win.

export async function preprocessImage(
  src: string,
  maxDim = 1280,
  quality = 0.85
): Promise<string> {
  try {
    const bitmap = await loadBitmapWithOrientation(src);
    const { width, height } = fit(bitmap.width, bitmap.height, maxDim);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return src;

    // Contrast/brightness boost — subtle, so we don't crush detail.
    // Safari supports canvas filter as of v15.4+.
    try {
      ctx.filter = 'contrast(1.08) brightness(1.03) saturate(1.05)';
    } catch {
      // Older Safari — skip filter, still get orientation + resize.
    }

    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close?.();
    return canvas.toDataURL('image/jpeg', quality);
  } catch {
    // If anything in the preprocess pipeline throws, fall back to raw src so
    // the solve still goes through. Server-side Vision will just work on the
    // unprocessed image.
    return src;
  }
}

// Backward-compat alias — existing callers import `compressImage`.
export const compressImage = preprocessImage;

async function loadBitmapWithOrientation(src: string): Promise<ImageBitmap> {
  // createImageBitmap with imageOrientation:'from-image' respects EXIF.
  // Modern Safari, Chrome, Firefox all support this.
  const blob = await (await fetch(src)).blob();
  try {
    return await createImageBitmap(blob, { imageOrientation: 'from-image' });
  } catch {
    // Very old browsers: fall back to Image loader (loses EXIF).
    const img = await loadImage(src);
    return (await createImageBitmap(img)) as ImageBitmap;
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function fit(w: number, h: number, maxDim: number) {
  if (w <= maxDim && h <= maxDim) return { width: w, height: h };
  const ratio = w / h;
  return w >= h
    ? { width: maxDim, height: Math.round(maxDim / ratio) }
    : { width: Math.round(maxDim * ratio), height: maxDim };
}
